import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Breadcrumb, BackdropCircular } from 'components';
import {
  Badge,
  Card,
  Typography,
  Divider,
  Grid,
  Switch,
  TextField,
  Icon,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
} from '@material-ui/core';
import {
  getHomepageBlogBlockConfigsApi,
  saveHomepageBlogBlockConfigsApi
} from 'api';


const blogGroup = ['first', 'second', 'third'];
const urlRegExp =  /^https:\/\/(.*)/;

const blogBlock = () => {
  const dispatch = useDispatch();
  const {
    homepageBlogBlockConfigs = [],
    loadingHomepageBlogBlockConfigs,
    savingHomepageBlogBlockConfigs
  } = useSelector((state) => state.store_configs);

  const loading = loadingHomepageBlogBlockConfigs ||
    savingHomepageBlogBlockConfigs;

  useEffect(() => {
    getHomepageBlogBlockConfigsApi({dispatch});
  }, []);

  const handleSave = (values) => {
    const formikConfigs = Object.values(values).reduce((acc, value) => {
      for (const key in value) {
        acc = [
          ...acc,
          {
            path: key,
            value: value[key]
          }
        ];
      }

      return acc;
    }, []);

    const initialConfigs = Object.values(homepageBlogBlockConfigs).reduce((acc, value) => {
      for (const key in value) {
        acc = [
          ...acc,
          {
            path: key,
            value: value[key]
          }
        ];
      }

      return acc;
    }, []);

    const configs = formikConfigs.filter((e) => initialConfigs.findIndex((i) => i.path === e.path && i.value == e.value) === -1);

    saveHomepageBlogBlockConfigsApi({dispatch, configs});
  };


  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <Breadcrumb
        routeSegments={[
          { name: 'Settings', path: '/settings#go_live_requirements' },
          { name: 'UI Configurations', path: '/settings#UI_configurations' },
          { name: 'Homepage Settings', path: '/configurations/homepage'},
          { name: 'Blog'}
        ]}
      />
      {!!Object.keys(homepageBlogBlockConfigs['general']).length &&
      <Formik
        initialValues={homepageBlogBlockConfigs}
        validationSchema={
          yup.object({
            general: yup.object({
              'store_info/premium_plus_blog/is_premium_plus_blog_enabled': yup
                .string(),
              'store_info/premium_plus_blog/premium_plus_blog_title': yup
                .string()
                .required('Field is required'),
              'store_info/premium_plus_blog/premium_plus_blog_url': yup
                .string()
                .matches(urlRegExp, 'Link must start with https://')
            }),
            first: yup.object().shape({
              'store_info/premium_plus_blog/first_item_image': yup
                .string()
                .ensure()
                .nullable()
                .test(
                  'first-item-image',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                )
                .test(
                  'first-item-image',
                  'Link must start with https://',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    const isUrl = val?.match(urlRegExp);
                    return isBlockEnable ? Boolean(isUrl) : true;
                  }
                ),
              'store_info/premium_plus_blog/first_item_text': yup
                .string()
                .test(
                  'first-item-text',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                ),
              'store_info/premium_plus_blog/first_item_button_text': yup
                .string()
                .test(
                  'first-item-button-text',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                ),
              'store_info/premium_plus_blog/first_item_button_link': yup
                .string()
                .test(
                  'first-item-button-link',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                )
                .test(
                  'first-item-button-link',
                  'Link must start with https://',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    const isUrl = val?.match(urlRegExp);
                    return isBlockEnable ? Boolean(isUrl) : true;
                  }
                ),
            }),
            second: yup.object().shape({
              'store_info/premium_plus_blog/second_item_image': yup
                .string()
                .test(
                  'second-item-image',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                )
                .test(
                  'second-item-image',
                  'Link must start with https://',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    const isUrl = val?.match(urlRegExp);
                    return isBlockEnable ? Boolean(isUrl) : true;
                  }
                ),
              'store_info/premium_plus_blog/second_item_text': yup
                .string()
                .test(
                  'second-item-text',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                ),
              'store_info/premium_plus_blog/second_item_button_text': yup
                .string()
                .test(
                  'second-item-button-text',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                ),
              'store_info/premium_plus_blog/second_item_button_link': yup
                .string()
                .test(
                  'second-item-button-link',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                )
                .test(
                  'second-item-button-link',
                  'Link must start with https://',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    const isUrl = val?.match(urlRegExp);
                    return isBlockEnable ? Boolean(isUrl) : true;
                  }
                ),
            }),
            third: yup.object().shape({
              'store_info/premium_plus_blog/third_item_image': yup
                .string()
                .test(
                  'third-item-image',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                )
                .test(
                  'third-item-image',
                  'Link must start with https://',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    const isUrl = val?.match(urlRegExp);
                    return isBlockEnable ? Boolean(isUrl) : true;
                  }
                ),
              'store_info/premium_plus_blog/third_item_text': yup
                .string()
                .test(
                  'third-item-text',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                ),
              'store_info/premium_plus_blog/third_item_button_text': yup
                .string()
                .test(
                  'third-item-button-text',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                ),
              'store_info/premium_plus_blog/third_item_button_link': yup
                .string()
                .test(
                  'third-item-button-link',
                  'Field is required',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    return isBlockEnable ? Boolean(val) : true;
                  }
                )
                .test(
                  'third-item-button-link',
                  'Link must start with https://',
                  (val, values) => {
                    const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1;
                    const isUrl = val?.match(urlRegExp);
                    return isBlockEnable ? Boolean(isUrl) : true;
                  }
                ),
            }),

          })
        }
        onSubmit={(values) => handleSave(values)}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
          handleSubmit
        }) => (

          <form autoComplete="off" className="p-8" onSubmit={handleSubmit}>
            <div className="m-10">
              <Card className="p-8">
                <Typography variant="h4" className="mb-8">Blog</Typography>
                <Divider className="mb-8" />
                <Grid container spacing={3} className="mb-8">
                  <Grid item xs={12} sm={2} className="my-2 ml-4">Enable</Grid>
                  <Grid item xs={12} sm={9}>
                    <Switch
                      color="primary"
                      name={'general[store_info/premium_plus_blog/is_premium_plus_blog_enabled]'}
                      checked={values.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1}
                      onChange={(event) => setFieldValue(
                        'general[store_info/premium_plus_blog/is_premium_plus_blog_enabled]',
                        event.target.checked ? '1' : '0'
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2} className="ml-4">Main Title <span className="text-error">*</span></Grid>
                  <Grid item xs={12} sm={9}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      name={'general.store_info/premium_plus_blog/premium_plus_blog_title'}
                      label="Title"
                      value={values.general['store_info/premium_plus_blog/premium_plus_blog_title']}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputProps={{
                        shrink: true
                      }}
                      error={
                        touched?.general?.['store_info/premium_plus_blog/premium_plus_blog_title'] &&
                        Boolean(errors?.general?.['store_info/premium_plus_blog/premium_plus_blog_title'])
                      }
                      helperText={
                        touched?.general?.['store_info/premium_plus_blog/premium_plus_blog_title'] &&
                        errors?.general?.['store_info/premium_plus_blog/premium_plus_blog_title']
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={2} className="ml-4">URL Address </Grid>
                  <Grid item xs={12} sm={9}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      name={'general.store_info/premium_plus_blog/premium_plus_blog_url'}
                      label="URL"
                      value={values.general['store_info/premium_plus_blog/premium_plus_blog_url']}
                      onChange={handleChange}
                      inputProps={{
                        shrink: true
                      }}
                      onBlur={handleBlur}
                      error={
                        touched?.general?.['store_info/premium_plus_blog/premium_plus_blog_url'] &&
                        Boolean(errors?.general?.['store_info/premium_plus_blog/premium_plus_blog_url'])
                      }
                      helperText={
                        touched?.general?.['store_info/premium_plus_blog/premium_plus_blog_url'] &&
                        errors?.general?.['store_info/premium_plus_blog/premium_plus_blog_url']
                      }
                    />
                  </Grid>
                </Grid>
                {values.general['store_info/premium_plus_blog/is_premium_plus_blog_enabled'] == 1 && blogGroup.map((item, index) => {
                  const isTouchedImageLink = touched?.[item]?.[`store_info/premium_plus_blog/${item}_item_image`];
                  const imageLinkErrorMessage = errors?.[item]?.[`store_info/premium_plus_blog/${item}_item_image`];

                  const isTouchedContent = touched?.[item]?.[`store_info/premium_plus_blog/${item}_item_text`];
                  const contentErrorMessage = errors?.[item]?.[`store_info/premium_plus_blog/${item}_item_text`];

                  const isTouchedButtonText = touched?.[item]?.[`store_info/premium_plus_blog/${item}_item_button_text`];
                  const buttonTextErrorMessage = errors?.[item]?.[`store_info/premium_plus_blog/${item}_item_button_text`];

                  const isTouchedButtonLinkRedirect = touched?.[item]?.[`store_info/premium_plus_blog/${item}_item_button_link`];
                  const buttonLinkRedirectErrorMessage = errors?.[item]?.[`store_info/premium_plus_blog/${item}_item_button_link`];

                  const isError = imageLinkErrorMessage || contentErrorMessage || buttonTextErrorMessage || buttonLinkRedirectErrorMessage;

                  return (
                    <Accordion elevation={0} key={index}>
                      <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                        <Badge badgeContent={Boolean(isError) ? '!' : null} color="error">
                          <Typography variant="h3" className="my-4" style={{textTransform: 'capitalize'}}>{item} Blog Item:</Typography>
                        </Badge>
                      </AccordionSummary>
                      <AccordionDetails className="flex-column">
                        <Grid container spacing={3} className="mb-8">
                          <Grid item xs={12} sm={2} className="ml-4">Image Link <span className="text-error">*</span></Grid>
                          <Grid item xs={12} sm={9}>
                            <TextField
                              required
                              fullWidth
                              variant="outlined"
                              label="Image URL"
                              name={`${item}.store_info/premium_plus_blog/${item}_item_image`}
                              onChange={handleChange}
                              value={values[item]?.[`store_info/premium_plus_blog/${item}_item_image`]}
                              onBlur={handleBlur}
                              inputProps={{
                                shrink: true
                              }}
                              helperText={isTouchedImageLink && imageLinkErrorMessage}
                              error={isTouchedImageLink && Boolean(imageLinkErrorMessage)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2} className="ml-4">Content <span className="text-error">*</span></Grid>
                          <Grid item xs={12} sm={9}>
                            <TextField
                              required
                              fullWidth
                              multiline
                              rows={3}
                              variant="outlined"
                              name={`${item}.store_info/premium_plus_blog/${item}_item_text`}
                              label="Content"
                              value={values[item]?.[`store_info/premium_plus_blog/${item}_item_text`]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              inputProps={{
                                shrink: true,
                                maxLength: 100
                              }}
                              helperText={(isTouchedContent && contentErrorMessage) ||
                                `You have written ${values[item]?.[`store_info/premium_plus_blog/${item}_item_text`].length}/100 characters`
                              }
                              error={isTouchedContent && Boolean(contentErrorMessage)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2} className="ml-4">Button Text <span className="text-error">*</span></Grid>
                          <Grid item xs={12} sm={9}>
                            <TextField
                              required
                              fullWidth
                              variant="outlined"
                              name={`${item}.store_info/premium_plus_blog/${item}_item_button_text`}
                              label="Button Text"
                              value={values[item]?.[`store_info/premium_plus_blog/${item}_item_button_text`]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              inputProps={{
                                shrink: true
                              }}
                              helperText={isTouchedButtonText && buttonTextErrorMessage}
                              error={isTouchedButtonText && Boolean(buttonTextErrorMessage)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2} className="ml-4">Button Link Redirect <span className="text-error">*</span></Grid>
                          <Grid item xs={12} sm={9}>
                            <TextField
                              required
                              fullWidth
                              variant="outlined"
                              name={`${item}.store_info/premium_plus_blog/${item}_item_button_link`}
                              label="Link Redirect"
                              value={values[item]?.[`store_info/premium_plus_blog/${item}_item_button_link`]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              inputProps={{
                                shrink: true
                              }}
                              helperText={isTouchedButtonLinkRedirect && buttonLinkRedirectErrorMessage}
                              error={isTouchedButtonLinkRedirect && Boolean(buttonLinkRedirectErrorMessage)}
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Card>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="mt-10 mb-5"
                >
                  Save Configurations
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
      }
      <BackdropCircular loading={loading} />
    </div>
  );
};

export default blogBlock;
